import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Toaster } from "sonner";
import { Layout, PageLoading } from "./components/Layout";
import { useHotelProvider } from "@/hooks/useHotelProvider";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/featureFlags";
import { SCRAPE_HOTELS_PATH } from "@/lib/scrapeAuth";

const HomePage = lazy(() =>
  import("./pages/HomePage").then((module) => ({ default: module.HomePage }))
);
const TravelPage = lazy(() =>
  import("./pages/TravelPage").then((module) => ({
    default: module.TravelPage,
  }))
);
const SearchResultsPage = lazy(() =>
  import("./pages/SearchResultsPageDense").then((module) => ({
    default: module.SearchResultsPageDense,
  }))
);
const HotelPropertyPage = lazy(() => import("./pages/HotelPropertyPageDense"));
const HotelBookingPage = lazy(() => import("./pages/HotelBookingPage"));
const BookingConfirmationPage = lazy(() =>
  import("./pages/BookingConfirmation").then((module) => ({
    default: module.BookingConfirmationPage,
  }))
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  }))
);
const PointsPage = lazy(() =>
  import("./pages/PointsPage").then((module) => ({
    default: module.PointsPage,
  }))
);
const SchedulePage = lazy(() =>
  import("./pages/SchedulePage").then((module) => ({
    default: module.SchedulePage,
  }))
);
const BookingsPage = lazy(() =>
  import("./pages/BookingsPage").then((module) => ({ default: module.BookingsPage }))
);
const ProfilePage = lazy(() =>
  import("./pages/ProfilePage").then((module) => ({ default: module.ProfilePage }))
);
const SignInPage = lazy(() =>
  import("./pages/SignInPage").then((module) => ({ default: module.SignInPage }))
);
const SignUpPage = lazy(() =>
  import("./pages/SignUpPage").then((module) => ({ default: module.SignUpPage }))
);
const SSOCallbackPage = lazy(() =>
  import("./pages/SSOCallbackPage").then((module) => ({
    default: module.SSOCallbackPage,
  }))
);
const AthleteAuthPage = lazy(() => import("./pages/AthleteAuthPage"));
const AmbassadorAuthPage = lazy(() => import("./pages/AmbassadorAuthPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AthletePage = lazy(() => import("./pages/AthletePage"));
const AmbassadorPage = lazy(() => import("./pages/AmbassadorPage"));
const SupportPage = lazy(() =>
  import("./pages/SupportPage").then((module) => ({ default: module.SupportPage }))
);
const AthleteReferralRedirect = lazy(
  () => import("./pages/AthleteReferralRedirect")
);
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const TermsOfServicePage = lazy(() =>
  import("./pages/TermsOfServicePage").then((module) => ({
    default: module.TermsOfServicePage,
  }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/PrivacyPolicyPage").then((module) => ({
    default: module.PrivacyPolicyPage,
  }))
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((module) => ({
    default: module.NotFoundPage,
  }))
);

const AUTH_BYPASS_ENABLED = import.meta.env.VITE_SCRAPE_AUTH_BYPASS === "true";

const NativeHotelRoute = ({ children }: { children: React.ReactNode }) => {
  const provider = useHotelProvider();
  const location = useLocation();
  const isAccessTravelPath =
    location.pathname.startsWith("/search/hotels") ||
    location.pathname.startsWith("/search/parks") ||
    location.pathname.startsWith("/search/cars") ||
    location.pathname.startsWith("/search/flights");

  if (AUTH_BYPASS_ENABLED) {
    return <>{children}</>;
  }

  if (provider === "access-dev") {
    if (isAccessTravelPath) {
      return <TravelPage />;
    }
    return <Navigate to={`/search/hotels${location.search}`} replace />;
  }

  // Native (non-AccessDev) providers have no parks/cars/flights inventory yet —
  // send those searches to hotels instead of rendering an empty placeholder.
  if (
    location.pathname.startsWith("/search/cars") ||
    location.pathname.startsWith("/search/flights") ||
    location.pathname.startsWith("/search/parks")
  ) {
    return <Navigate to={`/search/hotels${location.search}`} replace />;
  }

  return <>{children}</>;
};

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  if (!isLoaded) return <PageLoading />;
  if (!isSignedIn) {
    if (location.pathname.startsWith("/athlete")) {
      return <Navigate to="/athlete/auth?mode=signin" replace />;
    }
    if (location.pathname.startsWith("/ambassador")) {
      return <Navigate to="/ambassador/auth?mode=signin" replace />;
    }
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/signin?redirect_url=${redirectUrl}`} replace />;
  }
  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  if (AUTH_BYPASS_ENABLED) {
    return <>{children}</>;
  }
  return <RequireAuth>{children}</RequireAuth>;
};

const LegacyAuthRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

const AppRoutes = () => {
  const pointsEnabled = useFeatureFlag(FEATURE_FLAGS.POINTS_DASHBOARD_ENABLED);
  return (
    <Routes>
      <Route
        path="athlete/auth/*"
        element={
          <Suspense fallback={<PageLoading />}>
            <AthleteAuthPage />
          </Suspense>
        }
      />

      <Route
        path="ambassador/auth/*"
        element={
          <Suspense fallback={<PageLoading />}>
            <AmbassadorAuthPage />
          </Suspense>
        }
      />

      <Route path="/" element={<Layout />}>
        <Route path="auth/signin/*" element={<SignInPage />} />
        <Route path="auth/signup/*" element={<SignUpPage />} />
        <Route path="auth/sso-callback" element={<SSOCallbackPage />} />

        <Route
          path="forgot-password"
          element={<Navigate to="/auth/signin?view=resetPassword" replace />}
        />
        <Route path="sign-in/*" element={<LegacyAuthRedirect to="/auth/signin" />} />
        <Route path="sign-up/*" element={<LegacyAuthRedirect to="/auth/signup" />} />
        <Route path="sso-callback" element={<Navigate to="/auth/sso-callback" replace />} />
        <Route path={SCRAPE_HOTELS_PATH.slice(1)} element={<TravelPage />} />
        <Route path="a/:code" element={<AthleteReferralRedirect />} />

        <Route
          index
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="bookings"
          element={
            <ProtectedRoute>
              <BookingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="points"
          element={
            pointsEnabled ? (
              <ProtectedRoute>
                <PointsPage />
              </ProtectedRoute>
            ) : (
              <Navigate to="/profile" replace />
            )
          }
        />
        <Route
          path="schedule"
          element={
            <ProtectedRoute>
              <SchedulePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile/*"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="search/:type"
          element={
            <ProtectedRoute>
              <NativeHotelRoute>
                <SearchResultsPage />
              </NativeHotelRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="hotels/:propertyId"
          element={
            <ProtectedRoute>
              <NativeHotelRoute>
                <HotelPropertyPage />
              </NativeHotelRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="hotels/:propertyId/book"
          element={
            <ProtectedRoute>
              <NativeHotelRoute>
                <HotelBookingPage />
              </NativeHotelRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="bookings/confirmation"
          element={
            <ProtectedRoute>
              <BookingConfirmationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/*"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="athlete/*"
          element={
            <ProtectedRoute>
              <AthletePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ambassador/*"
          element={
            <ProtectedRoute>
              <AmbassadorPage />
            </ProtectedRoute>
          }
        />
        <Route path="terms" element={<TermsOfServicePage />} />
        <Route path="terms-of-service" element={<TermsOfServicePage />} />
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route
          path="analytics"
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

export default function App() {
  return (
    <>
      <Suspense fallback={<PageLoading />}>
        <AppRoutes />
      </Suspense>
      <Toaster richColors position="top-right" />
    </>
  );
}
